import { InjectableClass } from '@fino/lib-injection';
import _ from 'lodash';

export class UserService extends InjectableClass {
  constructor(UserRoles, UserPermissions, jwtHelper, $cookies) {
    'ngInject';
    super({ UserRoles, UserPermissions, jwtHelper, $cookies });
  }

  get token() {
    return (
      sessionStorage.getItem('jwtToken') || this.$cookies.getObject('jwtToken')
    );
  }

  set token(jwtToken) {
    const decoded = this.jwtHelper.decodeToken(jwtToken);
    if (decoded && decoded.role === this.UserRoles.Guest) {
      sessionStorage.setItem('jwtToken', jwtToken);
    } else {
      this.$cookies.putObject('jwtToken', jwtToken, {
        secure: true,
        samesite: 'strict',
        path: '/',
      });
    }
  }

  unsetToken() {
    sessionStorage.removeItem('jwtToken');
    this.$cookies.remove('jwtToken');
  }

  get Roles() {
    return this.UserRoles;
  }

  get Permissions() {
    return this.UserPermissions;
  }

  canRead(ticketType) {
    return this.can(ticketType, 'Read');
  }

  canCreate(ticketType) {
    return this.can(ticketType, 'Create');
  }

  canHandle(ticketType) {
    // Get any "Handle-like" permission to check
    return (
      this.can(ticketType, 'Handle') ||
      this.can(ticketType, 'CaptureTransactions') ||
      this.can(ticketType, 'Complete')
    );
  }

  can(ticketType, permission) {
    const user = this.get();
    if (!user || !user.permissions) {
      return false;
    }

    const userPermissions = this.UserPermissions[ticketType][permission];
    if (angular.isArray(userPermissions)) {
      // Special treatment for multiple permission handles
      return _.intersection(user.permissions, userPermissions).length > 0;
    }
    return _.includes(user.permissions, userPermissions);
  }

  login(jwtToken) {
    this.token = jwtToken;
  }

  logout() {
    this.unsetToken();
  }

  get() {
    return this.token && this.jwtHelper.decodeToken(this.token);
  }

  getToken() {
    return this.token;
  }

  getTenant() {
    let token = this.get();
    return token && token.tenant;
  }

  getBranchIds() {
    let token = this.get();
    return token && token.branchIds;
  }

  getRole() {
    let token = this.get();
    return token && token.role;
  }

  getSub() {
    let token = this.get();
    return token && token.sub;
  }

  hasPermission(permission) {
    if (angular.isArray(permission)) {
      return _.some(permission, (p) => this.hasPermission(p));
    }
    const user = this.get();
    return user && user.permissions && _.includes(user.permissions, permission);
  }

  isSignedIn() {
    const signedIn = this.token && !this.jwtHelper.isTokenExpired(this.token);
    if (!signedIn) {
      this.logout();
    }
    return signedIn;
  }

  isCustomerEntryUser() {
    return this.getRole() === this.UserRoles.Guest;
  }

  // @obsolete
  isGuest() {
    return this.getRole() === this.UserRoles.Guest;
  }

  isCompliance() {
    return this.getRole() === this.UserRoles.Compliance;
  }

  isBackOffice() {
    return this.getRole() === this.UserRoles.BackOffice;
  }

  isBackOfficeDirect() {
    return this.getRole() === this.UserRoles.BackOfficeDirect;
  }

  isDataCapturingProvider() {
    return this.getRole() === this.UserRoles.DataCapturingProvider;
  }

  isFrontOffice() {
    return (
      this.getRole() === this.UserRoles.FrontOffice ||
      this.getRole() === this.UserRoles.FrontOfficeWithComplete
    );
  }

  isFrontOfficeWithStandingOrders() {
    return this.getRole() === this.UserRoles.FrontOfficeWithStandingOrders;
  }

  isFrontOfficeWithComplete() {
    return this.getRole() === this.UserRoles.FrontOfficeWithComplete;
  }

  isAdmin() {
    return this.getRole() === this.UserRoles.Admin;
  }

  isDwsOnly() {
    return this.getRole() === this.UserRoles.DWSOnly;
  }

  isRevision() {
    return this.getRole() === this.UserRoles.Revision;
  }

  // The test pilot is allowed to use experimental features not yet available for regular users
  isTestPilot() {
    return this.get().sub === 'support@fino.digital';
  }

  hasRole(role) {
    const userRole = this.getRole();
    if (angular.isArray(role)) {
      return _.some(role, (r) => userRole === r);
    }
    return userRole === role;
  }
}
